import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  FileText,
  Download,
  Calendar,
  CheckCircle2,
  Clock,
  Filter,
  AlertCircle,
  XCircle,
  Edit3
} from 'lucide-react';

interface SheetDocument {
  id: string;
  year: number;
  week_number: number;
  status: string;
  pdf_url: string | null;
  signed_at: string;
  rejection_reason: string | null;
  rejected_at: string | null;
}

export default function MyDocuments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // États des données
  const [documents, setDocuments] = useState<SheetDocument[]>([]);
  const [loading, setLoading] = useState(true);
  
  // États des filtres
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [weekFilter, setWeekFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('validated'); // 'validated' | 'pending'

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user) return;
      setLoading(true);
      
      try {
        // Construction de la requête dynamique
        let query = supabase
          .from('weekly_sheets')
          .select('id, year, week_number, status, pdf_url, signed_at, rejection_reason, rejected_at')
          .eq('employee_id', user.id)
          .eq('year', yearFilter);

        // Filtre par statut (Validé ou En attente)
        if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter);
        }

        // Filtre par semaine
        if (weekFilter !== 'all') {
          query = query.eq('week_number', parseInt(weekFilter));
        }

        const { data, error } = await query
          .order('year', { ascending: false })
          .order('week_number', { ascending: false });

        if (error) throw error;
        setDocuments(data as SheetDocument[]);
      } catch (err) {
        console.error("Erreur chargement documents:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [user, yearFilter, weekFilter, statusFilter]);

  // Helper pour le label de la semaine
  const getWeekLabel = (year: number, week: number) => {
    const d = new Date(year, 0, 1 + (week - 1) * 7);
    const dayOfWeek = d.getDay();
    const start = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const date = new Date(d.setDate(start));
    return `Semaine du ${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
  };

  // Helper pour le badge de statut
  const getStatusBadge = (status: string) => {
    if (status === 'validated') {
      return (
        <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '12px', color: '#10b981' }}>
          <CheckCircle2 size={24} />
        </div>
      );
    } else if (status === 'rejected') {
      return (
        <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '12px', color: '#ef4444' }}>
          <XCircle size={24} />
        </div>
      );
    } else {
      return (
        <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '12px', color: '#f59e0b' }}>
          <Clock size={24} />
        </div>
      );
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', paddingBottom: '80px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/')} 
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer', color:'#64748b' }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Mes Documents</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Historique de vos feuilles d'heures</p>
        </div>
      </div>

      {/* BARRE DE FILTRES */}
      <div style={{ background: 'white', padding: '15px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.9rem', fontWeight: '600' }}>
          <Filter size={18} /> Filtrer :
        </div>

        {/* Filtre Statut */}
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', background: '#f8fafc', color: '#334155', outline: 'none', cursor: 'pointer', fontWeight:'600' }}
        >
          <option value="validated">Validées</option>
          <option value="pending">En cours</option>
          <option value="rejected">Rejetées</option>
        </select>

        {/* Filtre Année */}
        <select 
          value={yearFilter} 
          onChange={(e) => setYearFilter(parseInt(e.target.value))}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', background: '#f8fafc', color: '#334155', outline: 'none', cursor: 'pointer' }}
        >
          <option value={2024}>Année 2024</option>
          <option value={2025}>Année 2025</option>
          <option value={2026}>Année 2026</option>
        </select>

        {/* Filtre Semaine */}
        <select 
          value={weekFilter} 
          onChange={(e) => setWeekFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', background: '#f8fafc', color: '#334155', outline: 'none', cursor: 'pointer', minWidth: '150px' }}
        >
          <option value="all">Toutes les semaines</option>
          {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
            <option key={w} value={w}>Semaine {w}</option>
          ))}
        </select>
      </div>

      {/* LISTE DOCUMENTS */}
      {loading ? (
        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '40px' }}>Chargement...</p>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          
          {documents.length === 0 ? (
             <div style={{ padding: '40px', background: 'white', borderRadius: '16px', textAlign: 'center', border: '1px dashed #cbd5e1', color: '#64748b' }}>
               <FileText size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
               <p style={{ fontWeight: '500' }}>Aucun document trouvé.</p>
               <p style={{ fontSize: '0.9rem' }}>Modifiez les filtres pour voir d'autres résultats.</p>
             </div>
          ) : (
            documents.map(doc => (
              <div 
                key={doc.id} 
                style={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '16px', 
                  padding: '20px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s'
                }}
              >
                {/* Info Document */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {getStatusBadge(doc.status)}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Semaine {doc.week_number}</h3>
                      <span style={{ fontSize: '0.8rem', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '6px', fontWeight: '600' }}>{doc.year}</span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={14} /> {getWeekLabel(doc.year, doc.week_number)}
                    </div>
                    {doc.status === 'pending' && (
                      <div style={{ fontSize:'0.75rem', color:'#f59e0b', marginTop:'4px', fontWeight:'500' }}>En attente de validation Direction</div>
                    )}
                    {doc.status === 'rejected' && doc.rejection_reason && (
                      <div style={{ fontSize:'0.8rem', color:'#ef4444', marginTop:'8px', background:'#fef2f2', padding:'8px 12px', borderRadius:'8px', border:'1px solid #fecaca' }}>
                        <strong>Motif du rejet :</strong> {doc.rejection_reason}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {doc.status === 'rejected' && (
                    <button
                      onClick={() => navigate(`/timesheet?year=${doc.year}&week=${doc.week_number}`)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 16px', background: '#fef3c7',
                        color: '#b45309', borderRadius: '10px',
                        border: '1px solid #fde68a', fontWeight: '600', fontSize: '0.9rem',
                        cursor: 'pointer', transition: 'background 0.2s'
                      }}
                    >
                      <Edit3 size={18} /> Modifier
                    </button>
                  )}
                  {doc.pdf_url ? (
                    <a
                      href={doc.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 16px', background: '#eff6ff',
                        color: '#2563eb', borderRadius: '10px',
                        textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem',
                        border: '1px solid #dbeafe', transition: 'background 0.2s'
                      }}
                    >
                      <Download size={18} /> PDF
                    </a>
                  ) : (
                    <span style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.8rem', color:'#ef4444', background:'#fef2f2', padding:'6px 12px', borderRadius:'8px' }}>
                      <AlertCircle size={14} /> PDF manquant
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}